import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import {
  LogoutIcon
} from '../utils/icons';
import LanguageSelector from './LanguageSelector';
import NotificationPreferencesButton from './NotificationPreferencesButton';
import BrandLogo from './BrandLogo';
import { useBrand } from '../brand/BrandProvider';
import { DEFAULT_COMPANY_BRAND } from '../brand/types';
import '../styles/components/_app-layout.scss';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { companySlug, loadBrandFromSlug, setBrand } = useBrand();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('student_token');
    localStorage.removeItem('student_user');
    const loginPath = companySlug ? `/login?company=${encodeURIComponent(companySlug)}` : '/login';
    if (companySlug) {
      void loadBrandFromSlug(companySlug);
    } else {
      setBrand(DEFAULT_COMPANY_BRAND);
    }
    navigate(loginPath);
  };

  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false);
    handleLogout();
  };

  return (
    <Box className="app-layout__container">
      {/* Header */}
      <Box className="app-layout__header">
        <Box className="app-layout__header-top">
          <Box className="app-layout__logo-container">
            <BrandLogo />
          </Box>
          <Box className="app-layout__header-actions">
            {/* Search button - commented for now, will be used later */}
            {/* <IconButton className="app-layout__header-button">
              <SearchIcon />
            </IconButton> */}
            <LanguageSelector className="app-layout__header-button" />
            <NotificationPreferencesButton className="app-layout__header-button app-layout__header-button--notifications" />
            <IconButton 
              className="app-layout__header-button"
              onClick={() => setLogoutDialogOpen(true)}
            >
              <LogoutIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box className="app-layout__content">
        {children}
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        PaperProps={{
          sx: {
            backgroundColor: 'var(--brand-shell-bg, #13322B) !important',
            opacity: '1 !important',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.3) !important',
          }
        }}
      >
        <DialogTitle id="logout-dialog-title">
          {t('appLayout.logoutConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            {t('appLayout.logoutConfirmMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleLogoutConfirm} color="primary" variant="contained" autoFocus>
            {t('appLayout.logout')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppLayout;

